import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  disconnect,
  PusherEvent,
  subscribeToChannel,
  unsubscribeFromChannel,
} from 'utils/pusher';
import PageVisibility from 'react-page-visibility';
import { settings } from 'settings';
import Fetch from 'utils/fetch';
import { Helmet } from 'react-helmet';
import { Redirect } from 'react-router-dom';
import {
  DeliveryInfo,
  fetchDelivery,
  fetchForwardedOrderInfo,
  fetchOrder,
  fetchOrderScans,
  fetchPutSendTrackingMessages,
  setContactlessDelivery,
} from '../../fetch/orderFetch';
import { Loader } from 'components/Loader/Loader';
import Alert from 'components/alerts/Alert';
import Button from 'components/buttons/Button';
import OrderMenuBar from './components/OrderMenuBar';
import DeliveryProgressBar from './components/DeliveryProgressBar';
import OrderContent from './components/OrderContent';
import EditOrderContent from './components/EditOrderContent';
import OrderChat from './components/OrderChat';
import './Order.scss';
import {
  Address as OrderAddress,
  AddressValidity,
  CreateNewOrder,
  Delivery,
  DeviationsWithAuthor,
  DeviationTypes,
  Error,
  ForwardedOrderInfo,
  OrderClazz,
  OrderStatus,
  ParcelScan,
  Region,
  UpdateOrderRequest,
} from '../../types/orderTypes';
import { BundleJson, CourierJson } from '../../types/bundleTypes';
import Swal from 'sweetalert2';
import {
  WarningDeviation,
  DeviationHistory,
  ReportDeviationPost,
  ReportCourierDeviationPost,
} from '../../components/Deviation';
import {
  ConfirmedMarker,
  CourierMarker,
  DirectionsMap,
  DirectionsMapMarker,
} from '../../components/maps/DirectionsMap';
import { updateOrderRequest } from '../orders/OrdersUtil';
import { plainToClass } from 'class-transformer';
import {
  bundleChannelSubscription,
  bundleEventName,
} from '../../const/pusherChannelEventConsts';
import { fetchDeviationsWithAuthor } from '../../fetch/deviationFetch';
import analyticsWrapper from '../../utils/analyticsWrapper';
import { setAddressLocation } from '../orders/AddressFix/postNewAddress';
import dayjs from 'dayjs';
import NewDeviationsView from './components/NewDeviationsView';
import { ForwardedOrderInfoComponent } from './components/ForwardedOrderInfoComponent';

const _alertStatus: {
  [status: string]: {
    name: string;
    type: 'primary' | 'info' | 'success' | 'warning' | 'danger' | '';
  };
} = {
  pending: { name: 'Order pending', type: 'warning' },
  accepted: { name: 'Order accepted', type: 'primary' },
  in_delivery: { name: 'Order in delivery', type: 'primary' },
  delivered: { name: 'Order delivered', type: 'success' },
  canceled: { name: 'Order canceled', type: 'danger' },
  ready: { name: 'Order is ready', type: 'primary' },
};

function alertStatus(status) {
  return _alertStatus[status] || { name: '', type: '' };
}

interface MatchParams {
  id: string;
}

export interface RouteComponentProps<P> {
  match: Match<P>;
  region: Region;
}

export interface OrderComponentClassProps {
  isLoading: boolean;
  orderId: string;
  order: OrderClazz;
  orderScans: ParcelScan[];
  bundle: BundleJson | null;
  region: Region;
  onRefreshOrder: () => void;
  onEditOrder: () => void;
  isOrderEditing: boolean;
  deliveryInfo?: DeliveryInfo;
  onDuplicateOrder: (newOrder: CreateNewOrder) => void;
  setOrder: Dispatch<SetStateAction<OrderClazz>>;
  forwardedOrderInfo?: ForwardedOrderInfo;
}

export interface Match<P> {
  params: P;
  isExact: boolean;
  path: string;
  url: string;
}

interface OrderComponentClassState {
  currentCourierLocation: {
    latitude: number;
    longitude: number;
  };
  courier?: CourierJson;
}

export default function OrderComponent(
  props: RouteComponentProps<MatchParams>
) {
  const orderId = props.match.params.id;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [order, setOrder] = useState<OrderClazz>();
  const [forwardedOrderInfo, setForwardedOrderInfo] =
    useState<ForwardedOrderInfo>();
  const [orderScans, setOrderScans] = useState<ParcelScan[]>([]);
  const [orderBundle, setOrderBundle] = useState<BundleJson>();
  const [showEditDeviation, setShowEditDeviation] = useState(false);
  const [showEditCourierDeviation, setShowEditCourierDeviation] =
    useState(false);
  const [isOrderUpdating, setIsOrderUpdating] = useState(false);
  const [isOrderEditing, setIsOrderEditing] = useState(false);
  const [deviations, setDeviations] = useState<DeviationsWithAuthor[]>([]);
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo>(null);
  const [orderDuplicate, setOrderDuplicate] = useState<CreateNewOrder>(null);

  const getOrder = async (orderId: string) => {
    if (orderId) {
      const fetchedOrder = await fetchOrder(orderId);
      setOrder(fetchedOrder);
      if (fetchedOrder) {
        setDeviations(await fetchDeviationsWithAuthor(orderId));
        setDeliveryInfo(await fetchDelivery(fetchedOrder.deliveryId));
      } else {
        const forwardedOrder = await fetchForwardedOrderInfo(orderId);
        setForwardedOrderInfo(forwardedOrder);
        console.log(forwardedOrder);
      }
    } else {
      setOrder(null);
    }
    setIsLoading(false);
    setIsOrderUpdating(false);
  };

  const getBundle = async (orderId: string) => {
    let bundlesResponse: Response;
    try {
      bundlesResponse = await Fetch.get({
        url: `${settings.newApiUrl}/order/${orderId}/bundles`,
      });
    } catch (e) {
      console.warn(`Error retrieving bundleId for order ${orderId}`, e);
    }

    if (bundlesResponse && bundlesResponse.ok) {
      const ordersBundles: BundleJson[] = await bundlesResponse.json();
      const bundleJson = ordersBundles.length > 0 ? ordersBundles[0] : null;
      const bundle: BundleJson =
        bundleJson && plainToClass(BundleJson, bundleJson);

      setOrderBundle(bundle);
    } else {
      setOrderBundle(null);
    }
  };

  const getScans = async (orderId: string) => {
    const scans = await fetchOrderScans(orderId);
    if (scans) {
      setOrderScans(scans);
    } else {
      setOrderScans([]);
    }
  };

  const loadData = async (orderId: string) => {
    getOrder(orderId);
    getBundle(orderId);
    getScans(orderId);
  };

  const updateData = () => {
    setIsOrderUpdating(true);
    loadData(orderId);
  };

  useEffect(() => {
    getOrder(orderId);
    getBundle(orderId);
    getScans(orderId);
  }, [orderId]);

  const fireUpdateWarning = (bundleId: string) =>
    Swal.fire({
      title: 'The order has been updated!',
      text: `Please press ok and the order ${orderId} will automatically refresh. After refresh, you will be able to edit order again.`,
      icon: 'warning',
      allowOutsideClick: false,
      confirmButtonText: 'Ok',
      confirmButtonColor: '#d33',
    })
      .then(result => updateData())
      .catch(error => console.error(error.message));

  useEffect(() => {
    subscribeToChannel(bundleChannelSubscription, bundleEventName, data => {
      const bundleId = orderBundle?.id;
      // TODO: Switch to check for orderID if property has been added to data event
      if (data.bundleId === bundleId) {
        if (isOrderEditing) {
          fireUpdateWarning(bundleId);
        } else {
          updateData();
        }
      }
    });
    return () => {
      unsubscribeFromChannel(bundleChannelSubscription);
    };
  });

  const onVisibilityChange = (isVisible: boolean) => {
    if (isVisible) {
      subscribeToChannel(bundleChannelSubscription, bundleEventName, data => {
        const bundleId = orderBundle?.id;
        if (data.bundleId === bundleId) {
          if (isOrderEditing) {
            fireUpdateWarning(bundleId);
          } else {
            updateData();
          }
        }
      });
    } else {
      disconnect();
    }
  };

  if (orderDuplicate) {
    console.log(orderDuplicate);
    return (
      <Redirect
        to={{
          pathname: '/new-order',
          state: { newOrder: orderDuplicate },
        }}
      />
    );
  }

  // Setting here to ge a grouping. Might want to change this, because it gives a double entry to GA.
  analyticsWrapper.pageview('/orders/<orderId>');
  return (
    <PageVisibility onChange={onVisibilityChange}>
      <div className="order-wrapper">
        <Helmet>
          <title>Order - {orderId}</title>
        </Helmet>
        {isOrderUpdating && (
          <div className="order-loader">
            <Loader type="spinner" size="xxl" />
          </div>
        )}
        <div className="order">
          {order && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  title={
                    showEditCourierDeviation === false
                      ? 'Edit courier deviation'
                      : 'Close edit courier deviation'
                  }
                  onClick={() =>
                    setShowEditCourierDeviation(!showEditCourierDeviation)
                  }
                />
                <Button
                  title={
                    showEditDeviation === false
                      ? 'Edit partner deviation'
                      : 'Close edit partner deviation'
                  }
                  onClick={() => setShowEditDeviation(!showEditDeviation)}
                />
              </div>
              <WarningDeviation order={order} />
              <DeviationHistory
                deviations={deviations.filter(
                  deviation => deviation.type === DeviationTypes.Partner
                )}
                type={DeviationTypes.Partner}
              />
              <DeviationHistory
                deviations={deviations.filter(
                  deviation => deviation.type === DeviationTypes.Courier
                )}
                type={DeviationTypes.Courier}
              />
              {showEditDeviation && (
                <>
                  <div style={{ height: '15px' }} />
                  <ReportDeviationPost order={order} />
                </>
              )}
              {showEditCourierDeviation && (
                <>
                  <div style={{ height: '15px' }} />
                  <ReportCourierDeviationPost order={order} />
                </>
              )}
            </>
          )}
        </div>

        <OrderComponentClass
          isLoading={isLoading}
          orderId={orderId}
          order={order}
          region={props.region}
          bundle={orderBundle}
          orderScans={orderScans}
          onRefreshOrder={() => loadData(orderId)}
          onEditOrder={() => setIsOrderEditing(!isOrderEditing)}
          isOrderEditing={isOrderEditing}
          deliveryInfo={deliveryInfo}
          onDuplicateOrder={newOrder => setOrderDuplicate(newOrder)}
          setOrder={setOrder}
          forwardedOrderInfo={forwardedOrderInfo}
        />
      </div>
    </PageVisibility>
  );
}

export class OrderComponentClass extends React.Component<
  OrderComponentClassProps,
  OrderComponentClassState
> {
  private locationUpdateChannel: string;
  private pusherSubscriptionAttempted: boolean;

  constructor(props) {
    super(props);
    this.locationUpdateChannel = null;
    this.pusherSubscriptionAttempted = false;

    this.state = {
      courier: undefined,
      currentCourierLocation: null,
    };
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.order && !this.pusherSubscriptionAttempted) {
      const { token, id } = this.props.order;
      this.subscribeForLocationUpdates(token);
      this.fetchCourierLocation(id);
    }
  }

  componentWillUnmount() {
    if (this.locationUpdateChannel) {
      unsubscribeFromChannel(this.locationUpdateChannel);
    }
  }

  fetchCourierLocation = async (orderId: string) => {
    const response = await Fetch.get({
      url: `${settings.newApiUrl}/order/${orderId}/courier`,
    });

    if (response.ok) {
      const courier: CourierJson = await response.json();

      if (courier && courier.latitude && courier.longitude) {
        this.setState({
          currentCourierLocation: {
            latitude: courier.latitude,
            longitude: courier.longitude,
          },
          courier,
        });
      }
    }
  };

  subscribeForLocationUpdates = async (token: string) => {
    this.pusherSubscriptionAttempted = true;
    const response = await Fetch.get({
      url: `${settings.newApiUrl}/order/token/${token}/channel-names`,
    });

    if (response.ok) {
      const channelNames = await response.json();
      if (channelNames.locationChannel) {
        this.locationUpdateChannel = channelNames.locationChannel;
        subscribeToChannel(
          this.locationUpdateChannel,
          PusherEvent.LOCATION_UPDATE,
          locationUpdate => {
            this.setState({
              currentCourierLocation: {
                latitude: parseFloat(locationUpdate.location.latitude),
                longitude: parseFloat(locationUpdate.location.longitude),
              },
            });
          }
        );
      }
    }
  };

  orderStateChanged = () => {
    this.props.onRefreshOrder();
  };

  updateOrderAddress = async (
    orderId: string,
    address: OrderAddress
  ): Promise<Error[]> => {
    const order = await fetchOrder(orderId);

    const request: UpdateOrderRequest = updateOrderRequest(order);
    request.destination.address = {
      city: address.city,
      country: address.country,
      postalCode: address.postalCode,
      streetName: address.streetName,
      streetNumber: address.streetNumber,
    };

    const response = await Fetch.put({
      url: `${settings.newApiUrl}/order/${order.id}`,
      body: request,
    });

    if (response.ok) {
      return [];
    } else if (response.status < 500) {
      const responseErrors: Error[] = await response.json();
      return responseErrors;
    } else {
      return [
        {
          message: `Something unknown went wrong changing the address of the order.`,
          propertyPath: '',
        },
      ];
    }
  };

  onAddressChange = (newAddress: OrderAddress) => {
    const html = `<div style="font-size: 1.3em;">
            <br/><br/>Please confirm the new address:
            <br/><br/>
            <div style="display: flex; flex-direction: column; align-items: flex-start; padding-left: 50px; font-weight: lighter;">
              <div>Street: <b style="font-weight: 500">${newAddress.streetName}</b></div>
              <div>Number: <b style="font-weight: 500">${newAddress.streetNumber}</b></div>
              <div>City: <b style="font-weight: 500">${newAddress.city}</b></div>
              <div>Postal code: <b style="font-weight: 500">${newAddress.postalCode}</b></div>
              <div>Country: <b style="font-weight: 500">${newAddress.country}</b></div>
            </div>
          </div>
        `;

    Swal.fire({
      html: html,
      title: 'Update address?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
    }).then(async result => {
      if (result.value) {
        const orderId = this.props.orderId;
        const updateResult = await this.updateOrderAddress(orderId, newAddress);
        if (updateResult.length === 0) {
          this.orderStateChanged();
        } else {
          const errorMessages = (updateResult as Error[])
            .map(
              ({ message, propertyPath }) =>
                `<div style="text-align: left; font-size: 1.5rem; margin-bottom: 5px;">Field: ${propertyPath}<br />Message: ${message}</div>`
            )
            .join('');
          const html = `<div>Sorry, the address could not be updated with that address because:<br /><br />${errorMessages}</div>`;

          await Swal.fire({
            title: 'Could not update address',
            html: html,
            icon: 'error',
          });
        }
      }
    });
  };

  onAddressLocationChange = (longitude: number, latitude: number) => {
    const html = `<div>
            The recipient address location could not be automatically resolved.
            <br/><br/>Please confirm the new location for the recipient.
            <br/><br/>
            <a
            href="https://maps.google.com/?q=${latitude},${longitude}"
            target="_blank"
            rel="noopener noreferrer">
            Click here to see the selected location in Google Maps
          </a>

          </div>
        `;

    Swal.fire({
      html: html,
      title: 'Update address location?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
    })
      .then(async result => {
        if (result.value) {
          const orderId = this.props.orderId;
          await setAddressLocation(orderId, longitude, latitude);
          this.orderStateChanged();
        }
      })
      .catch(error => console.error(error.message));
  };

  onDuplicateOrder = (): void => {
    const { order, onDuplicateOrder } = this.props;
    const todayWindow = {
      start: dayjs()
        .add(1, 'day')
        .hour(order.deliverFromDate.hour())
        .minute(order.deliverFromDate.minute())
        .second(0)
        .millisecond(0)
        .toDate(),
      end: dayjs()
        .add(1, 'day')
        .hour(order.deliverUntilDate.hour())
        .minute(order.deliverUntilDate.minute())
        .second(0)
        .millisecond(0)
        .toDate(),
    };
    const newOrder: CreateNewOrder = {
      origin: {
        name: order.pickupName,
        address: order.warehouseAddress || order.originAddress,
        email: order.pickupEmail,
        phoneCountryCode: order.pickupPhoneCountryCode,
        phoneNumber: order.pickupPhoneNumber,
        pickupWindows: [todayWindow],
      },
      destination: {
        name: order.recipientName,
        address: order.destinationAddress,
        email: order.recipientEmail,
        phoneCountryCode: order.recipientPhoneCountryCode,
        phoneNumber: order.recipientPhoneNumber,
        deliveryWindow: todayWindow,
        verifications: order.verifications,
      },
      parcels: order.parcels,
      product: order.product,
      orderReference: '',
      courierInstructions: order.noteForCourier,
      shipmentIdentifier: null,
      brand: order.brand,
      accountId: order.accountId,
    };
    onDuplicateOrder(newOrder);
  };

  onSendTrackingLink = (): void => {
    const { order } = this.props;

    Swal.fire({
      title: 'Send tracking link?',
      icon: 'question',
      text: `Please confirm sending the tracking link messages (Email and SMS) to recipient of order ${order.id}!`,
      showCancelButton: true,
      confirmButtonText: 'Send',
      cancelButtonText: 'Cancel',
    }).then(async result => {
      if (result.value) {
        const success = await fetchPutSendTrackingMessages(order.id);
        if (!success) {
          await Swal.fire({
            title: 'Error sending tracking messages',
            icon: 'error',
            text: `An error occured when trying to send tracking messages for order ${order.id}. Tracking messages not send again!`,
          });
        }
      }
    });
  };

  onSetContactlessDelivery = (
    delivery: Delivery,
    contactless: boolean
  ): void => {
    const { order } = this.props;

    const title = contactless ? 'Contactless delivery' : 'Personal delivery';
    const newState = contactless ? 'contactless' : 'personal';
    Swal.fire({
      title: title,
      icon: 'question',
      html: `Please confirm setting order ${order.id} as <b>${newState}</b> delivery.`,
      showCancelButton: true,
      focusCancel: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
    }).then(async result => {
      if (result.value) {
        const success = await setContactlessDelivery(
          delivery.token,
          delivery.deliveryPin,
          contactless
        );
        if (!success) {
          await Swal.fire({
            title: 'Error updating contactless delivery',
            icon: 'error',
            text: `An error occurred when trying to set order ${order.id} as ${newState} delivery.`,
          });
        } else {
          this.orderStateChanged();
        }
      }
    });
  };

  render() {
    const {
      isLoading,
      order,
      bundle,
      orderScans,
      isOrderEditing,
      onEditOrder,
      deliveryInfo,
      region,
      forwardedOrderInfo,
    } = this.props;
    const { currentCourierLocation, courier } = this.state;

    if (isLoading) {
      return <Loader type="tile" />;
    }
    if (!order) {
      if (forwardedOrderInfo) {
        return (
          <ForwardedOrderInfoComponent
            forwardedOrderInfo={forwardedOrderInfo}
          />
        );
      }
      const orderId = this.props.orderId;
      return (
        <div
          style={{
            margin: '0px auto',
            paddingTop: 20,
            textAlign: 'center',
          }}
        >
          <h3>Order {orderId} not found.</h3>
        </div>
      );
    }

    const bundleId = bundle?.id;

    const markers: DirectionsMapMarker[] = [
      {
        lat: order.originAddress.location.latitude,
        lng: order.originAddress.location.longitude,
        icon: { label: '1' },
      },
      {
        lat: order.recipientLatitude,
        lng: order.recipientLongitude,
        icon: { label: '2' },
      },
    ];

    let courierMarker: CourierMarker = null;
    if (currentCourierLocation) {
      courierMarker = {
        lat: currentCourierLocation.latitude,
        lng: currentCourierLocation.longitude,
        title: 'Courier',
      };
    }

    let confirmedMarker: ConfirmedMarker = null;
    if (order.deliveryConfirmedLocation) {
      confirmedMarker = {
        lat: Number.parseFloat(order.deliveryConfirmedLocation.latitude),
        lng: Number.parseFloat(order.deliveryConfirmedLocation.longitude),
        title: 'Delivery Confirmed',
      };
    }

    return (
      <div className="order">
        <OrderMenuBar
          order={order}
          bundleId={bundleId}
          editingOrder={isOrderEditing}
          onEditOrder={onEditOrder}
          onDuplicateOrder={() => this.onDuplicateOrder()}
          onOrderStateChanged={this.orderStateChanged}
          region={region}
        />
        <div style={{ height: 10 }} />

        {/* TODO This should really be handled by the progress bar, but showing when cancelled now */}
        {order.status === OrderStatus.Canceled ? (
          <Alert type={alertStatus(order.status).type}>
            {alertStatus(order.status).name}
          </Alert>
        ) : null}
        {order.status !== OrderStatus.Canceled &&
        order.recipientAddressValidity ===
          AddressValidity.NEEDS_MANUAL_VALIDATION ? (
          <Alert type={'warning'}>
            The recipient address could not be resolved and requires manual
            validation.
          </Alert>
        ) : null}
        <div style={{ height: 10 }} />

        <DeliveryProgressBar
          statuses={[
            { title: 'Order created', createdAt: order.created },
            { title: 'Order picked up', createdAt: order.pickedAt },
            { title: 'Order delivered', createdAt: order.deliveredAt },
          ]}
        />
        <div style={{ height: 10 }} />
        <NewDeviationsView orderId={order.id} />
        <div style={{ height: 10 }} />
        {isOrderEditing ? (
          <EditOrderContent
            newOrder={order}
            bundleId={bundleId}
            region={this.props.region}
          />
        ) : (
          <OrderContent
            courier={courier}
            order={order}
            scans={orderScans}
            bundle={bundle}
            deliveryInfo={deliveryInfo}
            onAddressLocationChange={this.onAddressLocationChange}
            onAddressChange={this.onAddressChange}
            onSendTrackingLink={this.onSendTrackingLink}
            onSetContactlessDelivery={this.onSetContactlessDelivery}
            region={this.props.region}
            updateData={this.orderStateChanged}
            setOrder={this.props.setOrder}
          />
        )}
        {!isOrderEditing && (
          <div>
            <div style={{ height: 30 }} />
            <DirectionsMap
              markers={markers}
              courierMarker={courierMarker}
              confirmedMarker={confirmedMarker}
            />
            <div style={{ height: 30 }} />
            {deliveryInfo && (
              <OrderChat order={order} delivery={deliveryInfo.delivery} />
            )}
            <div style={{ height: 30 }} />
          </div>
        )}
      </div>
    );
  }
}
